import { FC } from "react";
import { Box, IconButton, Sheet, Stack, Typography } from "@mui/joy";
import { useColorScheme } from "@mui/joy/styles";

import i18n from "src/utils/i18n";
import { useTranslation } from "react-i18next";
import { Navbar } from "./Navbar";
import { Sidebar } from "./Sidebar";
import { useIsPersian } from "../hooks/isPersian";

export type DashboardTemplatePropsType = {
  children?: JSX.Element;
};

export const DashboardTemplate: FC<DashboardTemplatePropsType> = ({
  children,
}) => {
  const { mode, setMode } = useColorScheme();
  const { t } = useTranslation();
  const isPersian = useIsPersian();

  const currentMode = mode === "light" ? "light" : "dark";

  const toggleTheme = () => {
    setMode(currentMode === "dark" ? "light" : "dark");
  };

  return (
    <Sheet
      sx={{
        minHeight: "100vh",
        bgcolor: "background.body",
        color: "text.primary",
        background:
          currentMode === "dark"
            ? "radial-gradient(circle at 12% 12%, rgba(111,155,255,0.20), transparent 30%), radial-gradient(circle at 85% 85%, rgba(45,212,191,0.13), transparent 30%), var(--joy-palette-background-body)"
            : "radial-gradient(circle at 12% 12%, rgba(24,93,231,0.14), transparent 30%), radial-gradient(circle at 85% 85%, rgba(45,212,191,0.13), transparent 30%), var(--joy-palette-background-body)",
      }}
    >
      <Stack
        direction="row"
        width="100%"
        height="100vh"
        maxHeight="100vh"
        overflow="hidden"
      >
        <Sidebar />

        <Stack width="100%" minWidth={0} position="relative">
          <Box
            component="main"
            sx={{
              height: "100%",
              overflowY: "auto",
              px: { xs: 2, sm: 3, md: 5 },
              py: { xs: 3, md: 5 },
              pb: { xs: 12, lg: 5 },
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 1180,
                mx: "auto",
                minHeight: "100%",
              }}
            >
              {children}
            </Box>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              position: "fixed",
              top: { xs: 16, md: 24 },
              zIndex: 50,
              [isPersian ? "left" : "right"]: { xs: 16, md: 28 },
            }}
          >
            <IconButton
              color="primary"
              variant="soft"
              size="lg"
              onClick={toggleTheme}
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                bgcolor: "background.surface",
                backdropFilter: "blur(18px)",
                boxShadow: "lg",
              }}
            >
              {currentMode === "dark" ? "☀️" : "🌙"}
            </IconButton>

            <IconButton
              color="primary"
              variant="soft"
              size="lg"
              onClick={() =>
                i18n.changeLanguage(t("switchLanguage").toLowerCase())
              }
              sx={{
                minWidth: 44,
                px: 1.4,
                borderRadius: "999px",
                bgcolor: "background.surface",
                backdropFilter: "blur(18px)",
                boxShadow: "lg",
              }}
            >
              <Typography level="title-sm" sx={{ color: "inherit" }}>
                {t("switchLanguage")}
              </Typography>
            </IconButton>
          </Stack>

          <Navbar />
        </Stack>
      </Stack>
    </Sheet>
  );
};
